import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
const PRIVACY_POLICY_URL = process.env.REACT_APP_PRIVACY_POLICY_URL || 'https://agniyaspices.com/privacy-policy';

const TestimonialReviewPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    rating: '5',
    testimonial: '',
    consent: false,
    website: '',
  });
  const [photo, setPhoto] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim() || !form.testimonial.trim()) {
      setError('Please fill required fields.');
      return;
    }
    if (!form.consent) {
      setError('Please accept the privacy policy.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('name', form.name.trim());
      payload.append('email', form.email.trim());
      payload.append('phone', form.phone.trim());
      payload.append('city', form.city.trim());
      payload.append('rating', form.rating);
      payload.append('testimonial', form.testimonial.trim());
      payload.append('consent', form.consent ? 'true' : 'false');
      payload.append('website', form.website || '');
      if (photo) payload.append('photo', photo);

      const response = await fetch(`${API_BASE}/api/testimonials`, {
        method: 'POST',
        body: payload,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to submit testimonial');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit testimonial. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Write a Review | Agniya Spices</title>
        <meta
          name="description"
          content="Share your experience with Agniya Spices. Your review helps families, chefs, and businesses across Kerala and India choose the right spices."
        />
        <link rel="canonical" href="https://agniyaspices.com/testimonials/review" />
        {/* Transactional form — keep it out of search index but allow link discovery */}
        <meta name="robots" content="noindex, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Write a Review | Agniya Spices" />
        <meta property="og:description" content="Share your experience with Agniya Spices and help others choose the right spices." />
        <meta property="og:url" content="https://agniyaspices.com/testimonials/review" />
        <meta property="og:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />
        <meta property="og:site_name" content="Agniya Spices" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Write a Review | Agniya Spices" />
        <meta name="twitter:description" content="Share your experience with Agniya Spices." />
        <meta name="twitter:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://agniyaspices.com" },
              { "@type": "ListItem", "position": 2, "name": "Testimonials", "item": "https://agniyaspices.com/testimonials" },
              { "@type": "ListItem", "position": 3, "name": "Write a Review", "item": "https://agniyaspices.com/testimonials/review" }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="review-header-logo">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">AGNIYA SPICES</div>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Home</Link>
            <Link to="/#about" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">About Us</Link>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#4a3a33] hover:text-[#8B2820] transition-colors focus:outline-none">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#8B2820] font-semibold transition-colors">Testimonials</Link>
            <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">FAQ</Link>
            <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Contact Us</Link>
          </nav>
        </div>
      </header>

      {/* Breadcrumb bar */}
      <nav className="pt-24 bg-white border-b border-[#e7ded7]" aria-label="Breadcrumb">
        <div className="container mx-auto px-4 py-3 flex items-center text-sm text-[#4a3a33]">
          <Link to="/" className="hover:text-[#8B2820] transition-colors">Home</Link>
          <ChevronRight className="mx-2 h-4 w-4 text-[#bbb5ae]" />
          <Link to="/testimonials" className="hover:text-[#8B2820] transition-colors">Testimonials</Link>
          <ChevronRight className="mx-2 h-4 w-4 text-[#bbb5ae]" />
          <span className="text-[#8B2820] font-semibold">Write a Review</span>
        </div>
      </nav>

      <section className="pt-10 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-5">
            <Link to="/testimonials">
              <Button variant="ghost" className="text-[#4a3a33] hover:text-[#8B2820]">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Testimonials
              </Button>
            </Link>
          </div>

          <Card className="border-[#e7ded7] shadow-lg">
            <CardHeader>
              <CardTitle className="text-3xl text-[#8B2820]">Share Your Experience</CardTitle>
              <CardDescription className="text-[#4a3a33]">
                Please submit your review. Your feedback helps us improve and serve you better.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted && (
                <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-semibold">Thank you for your review.</p>
                    <p className="text-sm">Your review has been submitted successfully. A confirmation email has been sent.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {!submitted && (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="Your full name"
                        className="border-[#d4ccc4] focus:border-[#8B2820]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="you@example.com"
                        className="border-[#d4ccc4] focus:border-[#8B2820]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder="Phone number"
                        className="border-[#d4ccc4] focus:border-[#8B2820]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={form.city}
                        onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                        placeholder="Your city"
                        className="border-[#d4ccc4] focus:border-[#8B2820]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rating *</Label>
                      <Input
                        type="number"
                        min={1}
                        max={5}
                        value={form.rating}
                        onChange={(e) => setForm((prev) => ({ ...prev, rating: e.target.value }))}
                        className="border-[#d4ccc4] focus:border-[#8B2820]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Photo (Optional)</Label>
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                        className="border-[#d4ccc4] focus:border-[#8B2820]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Your Review *</Label>
                    <Textarea
                      value={form.testimonial}
                      onChange={(e) => setForm((prev) => ({ ...prev, testimonial: e.target.value }))}
                      placeholder="Write your feedback"
                      className="border-[#d4ccc4] focus:border-[#8B2820] min-h-[130px]"
                    />
                  </div>

                  <input
                    type="text"
                    value={form.website}
                    onChange={(e) => setForm((prev) => ({ ...prev, website: e.target.value }))}
                    autoComplete="off"
                    tabIndex={-1}
                    className="hidden"
                    aria-hidden="true"
                  />

                  <label className="flex items-start gap-2 text-sm text-[#4a3a33]">
                    <Checkbox
                      checked={form.consent}
                      onCheckedChange={(checked) => setForm((prev) => ({ ...prev, consent: checked === true }))}
                    />
                    <span>
                      I agree to the{' '}
                      <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="text-[#8B2820] underline">
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>

                  <div className="pt-2">
                    <Button type="submit" disabled={submitting} className="bg-[#8B2820] hover:bg-[#6d1f19] text-white">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Submit Review
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialReviewPage;
